import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { EMPLOYEE_CONTRACT_ASSIGMENT_TYPES, EmployeeLinkWithNameLoading } from 'domain/shared/employee';
import type { FlatListTableProps, ListTableAction, ListTableColumn } from 'framework/ui/ui-builder';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { POSITION_SLOT_WORKER_TYPE_LABELS } from 'domain/shared/positionSlot';
import {
  EDITABLE_POSITION_FILL_REQUEST_STATUSES,
  HIGHLIGHTED_POSITION_FILL_REQUEST_STATUSES,
  POSITION_FILL_REQUEST_STATUS_LABELS,
} from '../constants';
import type { PositionFillRequestListItem } from '../types';
import { SimpleLinkField } from 'framework/ViewTypes';

const getColumns = (
  getDetailsUrl: (id: string) => string,
  getRecruitmentPoolUrl: (id: string) => string,
): ListTableColumn<PositionFillRequestListItem>[] => [
  {
    id: 'id',
    label: 'ID',
    width: 90,
    sortable: true,
    render: request => <SimpleLinkField to={getDetailsUrl(request.id)} value={request.id} />,
  },
  {
    id: 'request',
    label: 'Request',
    width: 220,
    sortable: true,
    render: request => request.requestDisplayName,
  },
  {
    id: 'businessFunction',
    label: 'Business function',
    width: 190,
    sortable: true,
    render: request => request?.businessFunctionLabel || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'workRateType',
    label: 'FTE',
    sortable: true,
    width: 110,
    render: request =>
      request?.positionSlot?.workRateType
        ? EMPLOYEE_CONTRACT_ASSIGMENT_TYPES[request?.positionSlot?.workRateType]
        : EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'category',
    label: 'Category',
    sortable: true,
    width: 130,
    render: request =>
      request.positionSlot ? POSITION_SLOT_WORKER_TYPE_LABELS[request.positionSlot.workerType] : EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'location',
    label: 'Location',
    width: 130,
    render: request => request.officesLabel || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    width: 115,
    render: request => POSITION_FILL_REQUEST_STATUS_LABELS[request.status],
  },
  {
    id: 'ownerAlias',
    label: 'Owner',
    width: 150,
    sortable: true,
    render: request =>
      request.ownerAlias ? (
        <EmployeeLinkWithNameLoading id={request.ownerAlias} defaultValue={request.ownerAlias} />
      ) : (
        EMPTY_VALUE_INDICATOR
      ),
  },

  // TODO Replace value(recruitmentPoolId) to name
  {
    id: 'recruitmentPoolId',
    label: 'Recruitment pool',
    width: 170,
    render: request =>
      request.recruitmentPoolId ? (
        <SimpleLinkField
          to={getRecruitmentPoolUrl(request.recruitmentPoolId)}
          value={request?.recruitmentPoolName ?? request.recruitmentPoolId}
        />
      ) : (
        EMPTY_VALUE_INDICATOR
      ),
  },
];

type PositionFillRequestTableActions = {
  canUpdate: boolean;
  onEdit: (request: PositionFillRequestListItem) => void;
  onCancel: (request: PositionFillRequestListItem) => void;
  canCancel: boolean;
};

const getActions = (config: PositionFillRequestTableActions): ListTableAction<PositionFillRequestListItem>[] => [
  {
    id: 'edit-position-fill-request',
    label: 'Edit position fill request',
    icon: <EditOutlinedIcon />,
    variant: 'outlined',
    tooltip: 'Edit position fill request',
    isVisible: row => config.canUpdate && EDITABLE_POSITION_FILL_REQUEST_STATUSES.includes(row.status),
    onClick: config.onEdit,
  },
  {
    id: 'cancel-position-fill-request',
    label: 'Cancel position fill request',
    icon: <BlockOutlinedIcon />,
    variant: 'outlined',
    color: 'error',
    tooltip: 'Cancel position fill request',
    isVisible: row => config.canCancel && EDITABLE_POSITION_FILL_REQUEST_STATUSES.includes(row.status),
    onClick: config.onCancel,
  },
];

export const getPositionFillRequestsTableConfig = (
  rows: PositionFillRequestListItem[],
  getDetailsUrl: (id: string) => string,
  getRecruitmentPoolUrl: (id: string) => string,
  highlightConfig: {
    highlighted: boolean;
    canActivate: boolean;
  },
  actions: PositionFillRequestTableActions,
): FlatListTableProps<PositionFillRequestListItem> => ({
  rows,
  columns: getColumns(getDetailsUrl, getRecruitmentPoolUrl),
  isRowHighlighted(row) {
    return (
      highlightConfig.canActivate &&
      highlightConfig.highlighted &&
      HIGHLIGHTED_POSITION_FILL_REQUEST_STATUSES.includes(row.status)
    );
  },
  rowActions: { actions: getActions(actions), width: 72 },
  rowHeight: 46,
});
